import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as api from '../api';

export default function VerifyUserPage({ theme, setTheme }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [msg, setMsg] = useState('');

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMsg('Invalid or missing verification token.');
      return;
    }

    const verify = async () => {
      try {
        await api.verifyUser(token);
        setStatus('success');
        setMsg('User verified successfully!');
      } catch (e) {
        setStatus('error');
        setMsg(e.message || 'Verification failed. The token may be expired or invalid.');
      }
    };
    
    verify();
  }, [token]);

  const isDark = true;

  return (
    <div style={{ minHeight: '100vh', boxSizing: 'border-box', display: 'flex', padding: 20, background: isDark ? '#0a0a0f' : '#f0f0f5', position: 'relative', overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ═══ ANIMATED BACKGROUND LAYERS ═══ */}
      <style>{`
        @keyframes blob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(60px, -80px) scale(1.15); }
          50% { transform: translate(-40px, 50px) scale(0.85); }
          75% { transform: translate(30px, 20px) scale(1.05); }
        }
        @keyframes blob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-70px, 50px) scale(0.9); }
          50% { transform: translate(50px, -40px) scale(1.1); }
          75% { transform: translate(-20px, -60px) scale(0.95); }
        }
        @keyframes blob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, 70px) scale(1.12); }
          66% { transform: translate(-60px, -30px) scale(0.88); }
        }
        @keyframes floatShape {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes floatShapeSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-120deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @keyframes gridSlide {
          0% { transform: translateY(0); }
          100% { transform: translateY(60px); }
        }
      `}</style>

      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
        {/* ── Layer 1: Animated gradient orbs ── */}
        <div style={{
          position: 'absolute', top: '-5%', left: '-5%', width: '50vw', height: '50vw', maxWidth: 700, maxHeight: 700,
          background: 'radial-gradient(circle, #991b1b 0%, transparent 70%)', borderRadius: '50%',
          filter: 'blur(100px)', opacity: isDark ? 0.4 : 0.2, zIndex: 0,
          animation: 'blob1 20s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute', bottom: '-10%', right: '-5%', width: '45vw', height: '45vw', maxWidth: 600, maxHeight: 600,
          background: 'radial-gradient(circle, #dc2626 0%, transparent 70%)', borderRadius: '50%',
          filter: 'blur(90px)', opacity: isDark ? 0.3 : 0.15, zIndex: 0,
          animation: 'blob2 25s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute', top: '40%', left: '50%', width: '30vw', height: '30vw', maxWidth: 400, maxHeight: 400,
          background: 'radial-gradient(circle, #7f1d1d 0%, transparent 70%)', borderRadius: '50%',
          filter: 'blur(80px)', opacity: isDark ? 0.25 : 0.1, zIndex: 0,
          animation: 'blob3 18s ease-in-out infinite'
        }} />
        {/* Accent blue-tinted orb for depth contrast */}
        <div style={{
          position: 'absolute', top: '60%', left: '15%', width: '20vw', height: '20vw', maxWidth: 300, maxHeight: 300,
          background: 'radial-gradient(circle, #1e1b4b 0%, transparent 70%)', borderRadius: '50%',
          filter: 'blur(80px)', opacity: isDark ? 0.2 : 0.08, zIndex: 0,
          animation: 'blob2 22s ease-in-out infinite reverse'
        }} />

        {/* ── Layer 2: Animated grid lines ── */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, opacity: isDark ? 0.06 : 0.04,
          backgroundImage: `
            linear-gradient(${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'} 1px, transparent 1px),
            linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          animation: 'gridSlide 8s linear infinite',
        }} />

        {/* ── Layer 3: Floating geometric shapes ── */}
        <div style={{
          position: 'absolute', top: '15%', right: '18%', width: 20, height: 20,
          border: `2px solid ${isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)'}`,
          transform: 'rotate(45deg)', zIndex: 0,
          animation: 'floatShape 6s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', top: '25%', left: '12%', width: 12, height: 12,
          borderRadius: '50%', background: isDark ? 'rgba(239,68,68,0.2)' : 'rgba(153,27,27,0.1)',
          zIndex: 0, animation: 'floatShapeSlow 8s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '20%', left: '20%', width: 28, height: 28,
          borderRadius: '50%', border: `2px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(153,27,27,0.12)'}`,
          zIndex: 0, animation: 'floatShape 10s ease-in-out infinite 1s',
        }} />
        <div style={{
          position: 'absolute', top: '70%', right: '12%', width: 16, height: 16, zIndex: 0,
          animation: 'floatShapeSlow 7s ease-in-out infinite 2s',
        }}>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, background: isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)', transform: 'translateY(-50%)' }} />
          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, background: isDark ? 'rgba(239,68,68,0.25)' : 'rgba(153,27,27,0.15)', transform: 'translateX(-50%)' }} />
        </div>
        <div style={{
          position: 'absolute', bottom: '35%', left: '8%', width: 10, height: 10,
          border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
          transform: 'rotate(45deg)', zIndex: 0,
          animation: 'floatShape 9s ease-in-out infinite 3s',
        }} />
        
        {/* Dot clusters */}
        <div style={{ position: 'absolute', top: '12%', left: '30%', zIndex: 0, animation: 'pulse 4s ease-in-out infinite' }}>
          {[0, 1, 2].map(r => (
            <div key={r} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {[0, 1, 2].map(c => (
                <div key={c} style={{ width: 3, height: 3, borderRadius: '50%', background: isDark ? 'rgba(239,68,68,0.3)' : 'rgba(153,27,27,0.15)' }} />
              ))}
            </div>
          ))}
        </div>
        <div style={{ position: 'absolute', bottom: '12%', right: '25%', zIndex: 0, animation: 'pulse 5s ease-in-out infinite 1.5s' }}>
          {[0, 1].map(r => (
            <div key={r} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {[0, 1, 2, 3].map(c => (
                <div key={c} style={{ width: 2.5, height: 2.5, borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)' }} />
              ))}
            </div>
          ))}
        </div>

        {/* ── Layer 4: Radial vignette overlay ── */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `radial-gradient(ellipse at center, transparent 40%, ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.08)'} 100%)`,
        }} />

        {/* ── Layer 5: Subtle noise texture ── */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, opacity: isDark ? 0.03 : 0.02,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }} />
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 560, margin: 'auto', padding: '40px 0' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className="login-logo-outer" style={{
            borderRadius: 24,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.4), 0 0 60px rgba(153,27,27,0.15)' : '0 12px 32px rgba(0,0,0,0.12)',
          }}>
            <img className="login-logo-inner" src="/rnl-logo.png" alt="ProjectFlow Logo" style={{ borderRadius: 16 }} />
          </div>
          <h1 className="login-title" style={{ fontWeight: 800, letterSpacing: '-0.04em', color: 'var(--text-primary)', textShadow: isDark ? '0 2px 20px rgba(0,0,0,0.5)' : 'none', margin: 0, textAlign: 'center' }}>Rifitel Nigeria Limited</h1>
          <div className="login-subtitle" style={{ fontWeight: 700, letterSpacing: '0.18em', color: '#6490f6ff', textShadow: isDark ? '0 0 20px rgba(153,27,27,0.4)' : 'none', textAlign: 'center' }}>MANAGEMENT SYSTEM</div>
        </div>

        <div className="login-box-padding" style={{
          background: isDark ? 'rgba(18, 18, 24, 0.65)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          width: '100%',
          border: `4px solid ${isDark ? '#9d97abff' : 'black'}`,
          borderRadius: 24,
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.5), 0 0 80px rgba(153,27,27,0.15), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)'
            : '0 20px 50px rgba(0,0,0,0.12), 0 0 60px rgba(153,27,27,0.08), 0 0 0 1px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 6, letterSpacing: '0.02em' }}>
            {greeting}
          </div>
          <h2 className="login-form-title" style={{ fontWeight: 700, textAlign: 'center', color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 20 }}>
            Account Verification
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {status === 'verifying' && (
              <div style={{ color: '#6490f6ff', fontSize: 16, textAlign: 'center', padding: '20px 0', fontWeight: '500' }}>
                <span style={{ display: 'inline-block', animation: 'spin 1.5s linear infinite', marginRight: 8 }}>⌛</span>
                Verifying your email... please wait.
              </div>
            )}

            {status === 'success' && (
              <>
                <div style={{ width: '100%', padding: '16px 20px', borderRadius: 8, fontSize: 16, background: 'rgba(100, 144, 246, 0.15)', color: '#6490f6ff', border: '1px solid rgba(100, 144, 246, 0.3)', textAlign: 'center', marginBottom: 24, boxSizing: 'border-box' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 18 }}>{msg}</div>
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>You can now sign in to your account.</div>
                </div>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 'bold',
                    background: '#6490f6ff', color: '#060505ff', border: 'none', borderRadius: 11,
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    boxShadow: '0 0 18px #6490f6ff', fontFamily: 'inherit'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
                >
                  Proceed to Login
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                 <div style={{ width: '100%', padding: '16px 20px', borderRadius: 8, fontSize: 16, background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)', textAlign: 'center', marginBottom: 24, boxSizing: 'border-box' }}>
                   <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                   <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 18 }}>Verification Failed</div>
                   <div style={{ fontSize: 14 }}>{msg}</div>
                 </div>
                 <button
                  onClick={() => navigate('/')}
                  style={{
                    width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 'bold',
                    background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-light)', borderRadius: 11,
                    cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
                >
                  Back to Login
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: 'white', letterSpacing: '0.05em' }}>
          © {new Date().getFullYear()} Rifitel Nigeria · All rights reserved
        </div>

      </div>
    </div>
  );
}
